import { apiErrorMessage } from '@agos/api-client'
import {
  getGetAttendanceSelfTodayQueryKey,
  useGetAttendanceSelfToday,
  usePostAttendanceSelf,
} from '@agos/api-client/hr'
import { T, formatElapsed, formatTime, tt, useAuth } from '@agos/shared'
import { cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { LogIn, LogOut, Loader2, Pencil } from 'lucide-react'
import { useEffect, useState } from 'react'

import { isCarriedOver, phaseOf, type AttendanceRecord } from './attendance-phase'
import { SelfAttendanceDialog } from './SelfAttendanceDialog'
import type { AttendanceRecord as DayRecord } from './time-sheet'

/**
 * *Príchod* and *Odchod*, in the sidebar — `FRONTEND.md` §9.32, a port of legacy's
 * `components/sidebar-attendance-widget.tsx`.
 *
 * `/attendance` was ported and this was not, so the manager had the timesheet and no way to punch one.
 *
 * **The `self` endpoints, not the manager's.** `/attendance` is `@RequirePermission("attendance")`; a
 * cook, a driver and a waiter do not have it, cannot open that page, and are precisely the people who
 * clock in. `GET /attendance/self/today` and `POST /attendance/self` are `@RequireAuth` — your own row,
 * no permission — which is what makes a widget the right answer here rather than a link.
 */

/** How often the running shift's counter is redrawn. Legacy's interval. */
const TICK_MS = 30_000

export function SidebarAttendance() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [now, setNow] = useState(() => Date.now())

  const { data, isLoading, isError } = useGetAttendanceSelfToday()
  const record = (data as { record?: AttendanceRecord | null } | undefined)?.record ?? null
  const phase = phaseOf(record)
  /* §9.761 - until 04:00 the server answers yesterday's open shift, and says so by a `workDate` before `today`. The
     punch is unchanged - *Odchod* closes whichever shift the server holds - so what changes here is the words. */
  const carriedOver = isCarriedOver(data)

  /* §9.760 - *Upraviť časy zmeny* only over an answer. **`isError` as well as the payload**, so a refetch that failed
     over an answer still held counts as failed - react-query keeps the last data beside the error, and that data may
     be the shift as it stood before somebody else corrected it - and **no pencil while there is no answer**, loading
     or paused offline: over a failed read the dialog would open blank, and saving it would write over a record nobody
     had seen - FB-811's shape. */
  const answered = !isError && data !== undefined
  /* What the dialog opens on, taken when the pencil is pressed: a refetch while it is open must not move the times
     somebody is typing over, nor the work date they are on. */
  const [editing, setEditing] = useState<{
    workDate: string
    carriedOver: boolean
    record: DayRecord | null
  } | null>(null)

  // Only while a shift is running: a ticking clock behind a screen that says *Pekný zvyšok dňa* is a
  // render every thirty seconds for a number nobody is reading.
  useEffect(() => {
    if (phase !== 'active') return undefined
    const timer = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(timer)
  }, [phase])

  const punch = usePostAttendanceSelf({
    mutation: {
      onSuccess: (answer) => {
        void queryClient.invalidateQueries({ queryKey: getGetAttendanceSelfTodayQueryKey() })
        const punched = (answer as { record?: AttendanceRecord } | undefined)?.record
        const arriving = phase === 'before'
        toast({
          title: arriving ? tt('Príchod zaznamenaný') : tt('Odchod zaznamenaný'),
          description: formatTime((arriving ? punched?.checkIn : punched?.checkOut) ?? new Date()),
        })
      },
      // A refusal is a 409 — already clocked in, already clocked out — and the backend says which.
      // Guessing at it here would put a second, possibly wrong explanation on the screen.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Chyba'),
          description: apiErrorMessage(caught, tt('Skúste to znova')),
        }),
    },
  })

  if (!user) return null

  const send = (event: 'check_in' | 'check_out') => punch.mutate({ data: { event } })
  const busy = punch.isPending

  return (
    <div data-sidebar-attendance={phase} className="px-2 pb-1">
      {/* §9.761 - above the chips, so the arrival time under it reads as yesterday's rather than this morning's. */}
      {carriedOver && (
        <p data-attendance-carried-over className="px-1 pb-1 text-[10px] font-bold text-amber-400">
          <T>Zmena zo včera</T>
        </p>
      )}
      {(record?.checkIn || record?.checkOut) && (
        <div className="flex items-center gap-1.5 px-1 pb-1.5">
          {record?.checkIn && (
            <span
              data-attendance-in
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-400"
            >
              <LogIn className="w-2.5 h-2.5" /> {formatTime(record.checkIn)}
            </span>
          )}
          {record?.checkOut && (
            <span
              data-attendance-out
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/15 text-blue-400"
            >
              <LogOut className="w-2.5 h-2.5" /> {formatTime(record.checkOut)}
            </span>
          )}
          {phase === 'active' && record?.checkIn && (
            <span className="text-[10px] font-bold text-sidebar-foreground/40 tabular-nums">
              {formatElapsed(record.checkIn, now)}
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="w-full py-2 flex items-center justify-center text-sidebar-foreground/40">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        </div>
      ) : (
        /* §9.760 - the punch and the pencil on one row, the pencil last, in all three states: before the arrival is
           somebody who came and forgot *Príchod*, during and after is a time pressed too early or too late. */
        <div className="flex items-center gap-1.5">
          {phase === 'finished' ? (
            <div className="flex-1 min-w-0 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-bold border border-blue-500/20">
              {/* The owner asked for the glyph to go: *"remove that emoji from there"*. The words are
                * the whole message. */}
              <T>Pekný zvyšok dňa</T>
            </div>
          ) : (
            <button
              type="button"
              data-attendance-punch={phase === 'before' ? 'check_in' : 'check_out'}
              disabled={busy}
              onClick={() => send(phase === 'before' ? 'check_in' : 'check_out')}
              className={cn(
                'flex-1 min-w-0 flex items-center justify-center gap-1.5 py-2 rounded-lg text-white font-bold text-xs transition-all disabled:opacity-60',
                phase === 'before'
                  ? 'bg-emerald-600 hover:bg-emerald-500'
                  : 'bg-orange-600 hover:bg-orange-500',
              )}
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : phase === 'before' ? (
                <LogIn className="w-3.5 h-3.5" />
              ) : (
                <LogOut className="w-3.5 h-3.5" />
              )}
              {phase === 'before' ? <T>Príchod</T> : <T>Odchod</T>}
            </button>
          )}
          {answered && (
            <button
              type="button"
              data-attendance-edit
              // Not *Upraviť ...*: the pencil is in the sidebar on every screen, and a search for a button named
              // *Upraviť* - a screen's own edit - would find it first. The dialog it opens keeps the heading.
              title={tt('Zmeniť časy zmeny')}
              aria-label={tt('Zmeniť časy zmeny')}
              onClick={() => setEditing({ workDate: data.workDate, carriedOver, record })}
              className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border border-sidebar-border text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-white/5 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {editing !== null && (
        <SelfAttendanceDialog
          workDate={editing.workDate}
          carriedOver={editing.carriedOver}
          record={editing.record}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
